import React, { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import Card from "../../components/Card";
import Image from "../../components/Image";
import PageLayout from "../../components/PageLayout";
import ScrollToTop from "../../components/utils/ScrollToTop";
import imageOfficeWorking from "../../images/office-working.png";
import { replaceLocation } from "../../utils/navigationUtils";

// The staff app normally makes the quote's lead within a second or two.
export const POLL_INTERVAL_MS = 1000;
export const WAIT_FOR_MS = 60 * 1000;

const SCREENS = {
  waiting: {
    title: "Getting your sign-up ready…",
    body: <p>This usually takes a few seconds.</p>,
  },
  // Blocked senders never get a lead or a quote email, so this doesn't point to the email.
  gaveUp: {
    title: "Sorry, we can’t sign you up online right now",
    body: (
      <p>
        Please call us on <a href="tel:+442038563339">020 3856 3339</a> and we’ll sign you up over the phone. We’re open
        Monday to Friday, 9am to 5pm.
      </p>
    ),
  },
  expired: {
    title: "This page has expired",
    body: (
      <p>
        To sign up, use the “Get started” link in your quote email, or <a href="/quote">get a new quote</a>. If you’ve
        already signed up, your next steps are in our welcome email.
      </p>
    ),
  },
};

// "Sign up now" on the quote page lands here. Once the quote's lead exists,
// this replaces itself with the sign-up page, so Back skips it.
const ContinueSignup = () => {
  const [screen, setScreen] = useState("waiting");

  useEffect(() => {
    const controller = new AbortController();
    let pollTimer;
    let reason = "no response";

    // A hard deadline, so a request that never answers still ends in the fallback.
    const giveUpTimer = setTimeout(() => {
      controller.abort();
      clearTimeout(pollTimer);
      Sentry.captureMessage("Quote-page sign-up hand-off gave up waiting", {
        level: "warning",
        tags: { action: "QuoteSignupHandOff" },
        extra: { reason },
      });
      setScreen("gaveUp");
    }, WAIT_FOR_MS);

    const poll = async () => {
      try {
        const resp = await fetch("/api/quote-signup", { signal: controller.signal });
        const body = resp.ok ? await resp.json() : null;
        if (controller.signal.aborted) return;
        if (resp.status === 404 || body?.leadId) {
          clearTimeout(giveUpTimer);
          if (body?.leadId) replaceLocation(`/signup/${body.leadId}`);
          else setScreen("expired");
          return;
        }
        reason = body?.status ?? resp.status;
      } catch (err) {
        if (controller.signal.aborted) return;
        reason = err.message;
      }
      pollTimer = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
    return () => {
      controller.abort();
      clearTimeout(pollTimer);
      clearTimeout(giveUpTimer);
    };
  }, []);

  const { title, body } = SCREENS[screen];
  return (
    <PageLayout>
      <ScrollToTop />
      <div role="status">
        <Card title={title} removeTopPadding>
          {body}
        </Card>
      </div>
      <Image src={imageOfficeWorking} alt="" fullWidth />
    </PageLayout>
  );
};

export default ContinueSignup;
